import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { SwalPremium } from '../../../utils/SwalPremium'; 

export default function DirectorioSedes({ usuario, onBackToMenu }) {
  const API_URL = process.env.REACT_APP_API_URL;

  const [directorio, setDirectorio] = useState([]);
  const [sedesCatalogo, setSedesCatalogo] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [contactoSeleccionado, setContactoSeleccionado] = useState(null);
  
  const [notificacion, setNotificacion] = useState({ texto: '', tipo: '' });

  const mostrarAviso = (texto, tipo = 'success') => {
    setNotificacion({ texto, tipo });
    setTimeout(() => setNotificacion({ texto: '', tipo: '' }), 4500);
  };

  const [formData, setFormData] = useState({
    sede: '', nombre_contacto: '', cargo_contacto: '', telefono: '', extension: '', correo: '', observaciones: ''
  });

  // CONFIGURACIÓN ABAC (Solo permisos CRUD, sin restringir visibilidad de sedes)
  const configABAC = useMemo(() => {
    const cargoUpper = String(usuario?.cargo || usuario?.rol || '').toUpperCase().trim();
    const esSuperAdmin = cargoUpper.includes('SUPER_ADMIN') || cargoUpper === 'SUPERADMIN';
    
    let parsed = {};
    try {
      let rawPermisos = usuario?.permisos_avanzados;
      if (typeof rawPermisos === 'string') {
        parsed = JSON.parse(rawPermisos);
        if (typeof parsed === 'string') parsed = JSON.parse(parsed); 
      } else {
        parsed = rawPermisos || {};
      }
    } catch (e) { 
      parsed = {}; 
    }

    const micros = parsed.micros ? parsed.micros : parsed;
    
    let reglaModulo = micros['Directorio'] || micros['GH-DIR'] || {};

    if (Object.keys(reglaModulo).length === 0) {
      const keyExacta = Object.keys(micros).find(k => k.trim().toLowerCase() === 'directorio');
      if (keyExacta) reglaModulo = micros[keyExacta];
    }

    const parseBool = (val) => {
      if (val === true || val === 1) return true;
      if (typeof val === 'string') {
        const lower = val.toLowerCase().trim();
        return lower === 'true' || lower === '1' || lower === 'on';
      }
      return false;
    };

    const moduloEsTrue = reglaModulo === true || reglaModulo === 'true' || reglaModulo === 1;

    const puedeCrear = esSuperAdmin || moduloEsTrue || parseBool(reglaModulo?.crear) || parseBool(reglaModulo?.Crear);
    const puedeEditar = esSuperAdmin || moduloEsTrue || parseBool(reglaModulo?.editar) || parseBool(reglaModulo?.Editar);
    const puedeEliminar = esSuperAdmin || moduloEsTrue || parseBool(reglaModulo?.eliminar) || parseBool(reglaModulo?.Eliminar);

    return { puedeCrear, puedeEditar, puedeEliminar, micros };
  }, [usuario]);

  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    'x-user-data': JSON.stringify(usuario || {}),
    'x-abac-permissions': JSON.stringify(configABAC.micros || {})
  }), [usuario, configABAC.micros]);

  const cargarDirectorio = useCallback(async () => {
    setCargando(true);
    try {
      const resSedes = await fetch(`${API_URL}/api/horarios/sedes`, { headers: getHeaders() });
      if (resSedes.ok) {
        const dataSedes = await resSedes.json();
        if (dataSedes?.data) setSedesCatalogo(dataSedes.data);
      }

      const resDir = await fetch(`${API_URL}/api/th/directorio/listar`, { headers: getHeaders() });
      if (resDir.ok) {
        const dataDir = await resDir.json();
        setDirectorio(Array.isArray(dataDir) ? dataDir : (dataDir.data || []));
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setCargando(false);
    }
  }, [API_URL, getHeaders]);

  useEffect(() => { cargarDirectorio(); }, [cargarDirectorio]);

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const abrirModalNuevo = () => {
    setModoEdicion(false);
    setContactoSeleccionado(null);
    setFormData({ sede: '', nombre_contacto: '', cargo_contacto: '', telefono: '', extension: '', correo: '', observaciones: '' });
    setModalAbierto(true);
  };

  const abrirModalEditar = (contacto) => {
    setModoEdicion(true);
    setContactoSeleccionado(contacto.id);
    setFormData({
      sede: contacto.sede || '',
      nombre_contacto: contacto.nombre_contacto || '',
      cargo_contacto: contacto.cargo_contacto || '',
      telefono: contacto.telefono || '',
      extension: contacto.extension || '',
      correo: contacto.correo || '',
      observaciones: contacto.observaciones || ''
    });
    setModalAbierto(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.sede.trim() || !formData.telefono.trim()) {
      mostrarAviso('⚠️ La sede y teléfono son obligatorios.', 'error');
      return;
    }
    try {
      const endpoint = modoEdicion ? `${API_URL}/api/th/directorio/editar/${contactoSeleccionado}` : `${API_URL}/api/th/directorio/crear`;
      const res = await fetch(endpoint, {
        method: modoEdicion ? 'PUT' : 'POST',
        headers: getHeaders(),
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (res.ok && data.success !== false) {
        mostrarAviso(`✅ Contacto ${modoEdicion ? 'actualizado' : 'agregado'} correctamente.`, 'success');
        setModalAbierto(false);
        cargarDirectorio();
      } else {
        mostrarAviso(`❌ Error: ${data.message || 'No se pudo guardar'}`, 'error');
      }
    } catch (e) {
      mostrarAviso('❌ Error de conexión.', 'error');
    }
  };

  const eliminarContacto = async (id) => {
    const confirmar = await SwalPremium.fire({
      title: '¿Eliminar contacto?',
      text: "Esta acción es permanente.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (confirmar.isConfirmed) {
      try {
        const res = await fetch(`${API_URL}/api/th/directorio/eliminar/${id}`, {
          method: 'DELETE',
          headers: getHeaders()
        });
        const data = await res.json();
        
        if (res.ok && data.success !== false) {
          mostrarAviso('✅ Contacto eliminado.', 'success');
          cargarDirectorio();
        } else {
          mostrarAviso(`❌ Error: ${data.message || 'No se pudo eliminar'}`, 'error');
        }
      } catch (error) {
        mostrarAviso('❌ Error de conexión.', 'error');
      }
    }
  };

  // AGRUPACIÓN GENERAL DE SEDES (Sin restricciones para que aparezcan todas)
  const sedesAgrupadas = useMemo(() => {
    const agrupado = {};
    directorio.forEach((item) => {
      const sedeNombre = String(item.sede || 'OFICINAS').toUpperCase().trim();
      if (!agrupado[sedeNombre]) agrupado[sedeNombre] = [];
      agrupado[sedeNombre].push(item);
    });

    return Object.entries(agrupado).filter(([sede, contactos]) => {
      if (!busqueda.trim()) return true;
      const q = busqueda.toLowerCase();
      return sede.toLowerCase().includes(q) || contactos.some(c => 
        (c.nombre_contacto || '').toLowerCase().includes(q) ||
        (c.cargo_contacto || '').toLowerCase().includes(q) ||
        (c.telefono || '').toLowerCase().includes(q)
      );
    });
  }, [directorio, busqueda]);

  return (
    <div className="w-full px-2 py-3 sm:p-6 animate-fade-in relative z-10">
      <div className="bg-white/55 backdrop-blur-[35px] rounded-[24px] sm:rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] p-4 sm:p-8 max-w-6xl mx-auto border border-white/80">
        
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b border-slate-300/60 pb-4 mb-5 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-sky-100 text-sky-600 flex items-center justify-center text-xl sm:text-2xl shadow-inner border border-sky-200 shrink-0">📞</div>
            <div>
              <h2 className="text-lg sm:text-2xl font-black text-slate-800 tracking-tight">Directorio Telefónico y Sedes</h2>
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mt-0.5">Números corporativos, extensiones y contactos clave por punto de venta</p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto justify-end">
            {configABAC.puedeCrear && (
              <button type="button" onClick={abrirModalNuevo} className="flex-1 md:flex-initial px-4 sm:px-5 py-2.5 rounded-xl bg-sky-600 hover:bg-sky-500 text-white text-xs font-black uppercase tracking-wider transition-all shadow-md cursor-pointer text-center">+ Agregar Contacto / Extensión</button>
            )}
            <button type="button" onClick={onBackToMenu} className="px-4 sm:px-5 py-2.5 rounded-xl bg-white/90 hover:bg-white text-slate-800 text-xs font-black uppercase tracking-wider transition-all border border-slate-200 shadow-sm cursor-pointer">Volver</button>
          </div>
        </div>

        {notificacion.texto && (
          <div className={`mb-4 p-3.5 rounded-2xl text-xs font-black flex justify-between items-center border ${notificacion.tipo === 'error' ? 'bg-red-50 text-red-800 border-red-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'}`}>
            <span>{notificacion.texto}</span>
            <button onClick={() => setNotificacion({ texto: '', tipo: '' })} className="ml-3 font-black cursor-pointer">✕</button>
          </div>
        )}

        <div className="mb-6">
          <input type="text" placeholder="🔍 Buscar sede, nombre de colaborador, cargo o teléfono..." value={busqueda} onChange={(e) => setBusqueda(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-white/90 border border-slate-200 text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-sky-400 shadow-sm" />
        </div>

        {cargando ? (
          <div className="p-12 text-center text-slate-500 font-bold text-xs animate-pulse bg-white/70 rounded-2xl">Cargando directorio de sedes...</div>
        ) : sedesAgrupadas.length === 0 ? (
          <div className="p-12 text-center text-slate-400 font-bold text-xs italic bg-white/70 rounded-2xl">No se encontraron registros en el directorio.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {sedesAgrupadas.map(([sede, contactos]) => (
              <div key={sede} className="bg-white/80 backdrop-blur-md rounded-2xl p-5 border border-white shadow-sm hover:shadow-md transition-all flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-3">
                    <h3 className="text-sm font-black text-slate-900 uppercase flex items-center gap-2"><span>🏢</span> {sede}</h3>
                    <span className="bg-sky-100 text-sky-800 text-[10px] font-black px-2 py-0.5 rounded-full">{contactos.length} {contactos.length === 1 ? 'contacto' : 'contactos'}</span>
                  </div>

                  <div className="space-y-3">
                    {contactos.map((c) => (
                      <div key={c.id} className="bg-slate-50 p-3 rounded-xl border border-slate-100/80 text-xs relative group">
                        
                        <div className="absolute top-3 right-3 flex gap-2 z-20">
                          {configABAC.puedeEditar && (
                            <button 
                              onClick={(e) => { e.stopPropagation(); abrirModalEditar(c); }} 
                              style={{ width: '32px', height: '32px', flex: 'none', padding: 0 }}
                              className="flex items-center justify-center bg-white border border-slate-200 hover:bg-sky-50 rounded-lg shadow-sm cursor-pointer hover:scale-105 transition-transform"
                              title="Editar"
                            >
                              <span style={{ fontSize: '15px' }}>✏️</span>
                            </button>
                          )}
                          {configABAC.puedeEliminar && (
                            <button 
                              onClick={(e) => { e.stopPropagation(); eliminarContacto(c.id); }} 
                              style={{ width: '32px', height: '32px', flex: 'none', padding: 0 }}
                              className="flex items-center justify-center bg-white border border-slate-200 hover:bg-red-50 rounded-lg shadow-sm cursor-pointer hover:scale-105 transition-transform"
                              title="Eliminar"
                            >
                              <span style={{ fontSize: '15px' }}>🗑️</span>
                            </button>
                          )}
                        </div>

                        <p className="font-black text-slate-800 uppercase pr-20">{c.nombre_contacto || 'Línea General / Sede'}</p>
                        {c.cargo_contacto && <p className="text-[10px] font-bold text-sky-600 uppercase mb-1 pr-20">{c.cargo_contacto}</p>}
                        
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-slate-600 font-semibold">
                          <a href={`tel:${c.telefono}`} className="flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-lg border border-emerald-200 hover:bg-emerald-100 transition">📞 {c.telefono}</a>
                          {c.extension && <span className="bg-slate-200 text-slate-700 px-2 py-1 rounded-lg text-[10px]">Ext: {c.extension}</span>}
                        </div>
                        {c.correo && <p className="text-[11px] text-slate-500 mt-1.5 truncate">✉️ {c.correo}</p>}
                        {c.observaciones && <p className="text-[10px] text-slate-400 italic mt-1 border-t border-slate-200/60 pt-1">{c.observaciones}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modalAbierto && (
        <div className="fixed inset-0 z-[9999] bg-slate-900/65 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-[24px] sm:rounded-[28px] shadow-2xl p-5 sm:p-7 w-full max-w-lg border border-gray-100 my-auto">
            <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-3">
              <h3 className="text-base font-black text-slate-800">{modoEdicion ? 'Editar Contacto' : 'Agregar Contacto al Directorio'}</h3>
              <button type="button" onClick={() => setModalAbierto(false)} className="w-8 h-8 bg-gray-100 rounded-full text-gray-600 font-bold hover:bg-red-100 hover:text-red-600 cursor-pointer">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3.5">
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Sede / Punto de Venta *</label>
                <div className="relative">
                  <select name="sede" required value={formData.sede} onChange={handleInputChange} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-bold uppercase outline-none focus:border-sky-500 appearance-none cursor-pointer">
                    <option value="">-- SELECCIONE UNA SEDE --</option>
                    {sedesCatalogo.map((sede) => (
                      <option key={sede.id} value={sede.nombre}>{sede.nombre}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">▾</div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Nombre del Responsable</label><input type="text" name="nombre_contacto" value={formData.nombre_contacto} onChange={handleInputChange} placeholder="Ej: Juan Pérez" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-bold uppercase outline-none focus:border-sky-500" /></div>
                <div><label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Cargo</label><input type="text" name="cargo_contacto" value={formData.cargo_contacto} onChange={handleInputChange} placeholder="Ej: Administrador" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-bold uppercase outline-none focus:border-sky-500" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Teléfono / Celular *</label><input type="text" name="telefono" required value={formData.telefono} onChange={handleInputChange} placeholder="Ej: 3101234567" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-bold outline-none focus:border-sky-500" /></div>
                <div><label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Extensión</label><input type="text" name="extension" value={formData.extension} onChange={handleInputChange} placeholder="Ej: 102" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-bold outline-none focus:border-sky-500" /></div>
              </div>
              <div><label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Correo Electrónico</label><input type="email" name="correo" value={formData.correo} onChange={handleInputChange} placeholder="sede@elgransurtidor.com" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-bold outline-none focus:border-sky-500" /></div>
              <div><label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Observaciones / Horarios</label><textarea name="observaciones" rows="2" value={formData.observaciones} onChange={handleInputChange} placeholder="Ej: Solo atiende de 8:00 AM a 5:00 PM" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-medium outline-none resize-none" /></div>
              <div className="pt-2 flex gap-2">
                <button type="button" onClick={() => setModalAbierto(false)} className="px-4 py-2.5 rounded-xl bg-gray-100 text-slate-700 font-bold text-xs cursor-pointer hover:bg-gray-200">Cancelar</button>
                <button type="submit" className="flex-1 py-2.5 rounded-xl bg-sky-600 hover:bg-sky-700 text-white font-black uppercase text-xs tracking-wider shadow-md cursor-pointer transition-colors">{modoEdicion ? 'Guardar Cambios' : 'Guardar Contacto'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}